import { ArrowDownNarrowWide, Plus, Sparkles, Trash2 } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { DatePicker } from "@/components/cinch/DatePicker";
import { SortableList } from "@/components/cinch/SortableList";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import {
  useCreateMilestone,
  useDeleteMilestone,
  useReorderMilestones,
  useToggleMilestone,
  useUpdateMilestone,
  type Milestone,
  type Objective,
} from "../api";
import { dateToIso, isoToDate } from "../constants";
import { dueDateOrder } from "../milestoneOrder";
import { MilestoneSuggestDialog } from "./MilestoneSuggestDialog";
import { NotesField } from "./NotesField";

export function MilestoneList({ objective, canWrite }: { objective: Objective; canWrite: boolean }) {
  const create = useCreateMilestone();
  const reorder = useReorderMilestones();
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newWeight, setNewWeight] = useState("");
  // Optimistic row order shown while the reorder round-trips; new/removed
  // milestones fall back to the server order.
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);

  const milestones = useMemo(() => {
    if (!pendingOrder) return objective.milestones;
    const byId = new Map(objective.milestones.map((m) => [m.id, m]));
    const ordered = pendingOrder.flatMap((id) => byId.get(id) ?? []);
    const seen = new Set(pendingOrder);
    return [...ordered, ...objective.milestones.filter((m) => !seen.has(m.id))];
  }, [objective.milestones, pendingOrder]);

  function applyOrder(orderedIds: string[]) {
    setPendingOrder(orderedIds);
    reorder.mutate({ objectiveId: objective.id, orderedIds });
  }

  function sortByDueDate() {
    const next = dueDateOrder(milestones);
    if (next.some((id, i) => id !== milestones[i]?.id)) applyOrder(next);
  }

  const weightTotal = objective.milestones.reduce((sum, m) => sum + m.weight, 0);

  function addMilestone() {
    const title = newTitle.trim();
    if (!title) return;
    create.mutate(
      {
        objectiveId: objective.id,
        payload: { title, weight: newWeight ? Number(newWeight) : 0 },
      },
      {
        onSuccess: () => {
          setNewTitle("");
          setNewWeight("");
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span
          className={cn(
            "text-xs",
            objective.weight_warning ? "text-warning" : "text-muted-foreground",
          )}
        >
          {weightTotal.toFixed(0)}% weighted
          {objective.weight_warning ? " — doesn't sum to 100" : ""}
        </span>
        {canWrite && (
          <div className="flex items-center gap-1">
            {milestones.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={sortByDueDate}
                disabled={reorder.isPending}
                title="Reorder milestones by due date, earliest first (undated last)"
              >
                <ArrowDownNarrowWide className="h-3.5 w-3.5" /> Sort by due date
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs"
              onClick={() => setSuggestOpen(true)}
            >
              <Sparkles className="h-3.5 w-3.5" /> AI: Suggest milestones
            </Button>
          </div>
        )}
      </div>

      {objective.milestones.length === 0 && (
        <p className="text-sm text-muted-foreground">No milestones yet.</p>
      )}

      <SortableList
        items={milestones}
        getId={(m) => m.id}
        onReorder={applyOrder}
        disabled={!canWrite}
        className="flex flex-col divide-y divide-border rounded-md border border-border"
        renderItem={(m, handle) => (
          <MilestoneRow milestone={m} canWrite={canWrite} handle={handle} />
        )}
      />

      {canWrite && (
        <div className="flex items-center gap-2 pt-1">
          <Input
            placeholder="New milestone…"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") addMilestone();
            }}
            className="flex-1"
          />
          <Input
            type="number"
            placeholder="wt %"
            value={newWeight}
            min={0}
            max={100}
            onChange={(e) => setNewWeight(e.target.value)}
            className="w-20"
          />
          <Button type="button" size="sm" onClick={addMilestone} disabled={!newTitle.trim()}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}

      {suggestOpen && (
        <MilestoneSuggestDialog
          objective={objective}
          onClose={() => setSuggestOpen(false)}
        />
      )}
    </div>
  );
}

function MilestoneRow({
  milestone,
  canWrite,
  handle,
}: {
  milestone: Milestone;
  canWrite: boolean;
  handle: ReactNode;
}) {
  const update = useUpdateMilestone();
  const toggle = useToggleMilestone();
  const remove = useDeleteMilestone();
  const [showNotes, setShowNotes] = useState(false);

  return (
    <div className="flex flex-col gap-1.5 px-3 py-2">
      <div className="flex items-center gap-2">
        {handle}
        <Checkbox
          checked={milestone.is_complete}
          disabled={!canWrite}
          onCheckedChange={(checked) =>
            toggle.mutate({ id: milestone.id, value: checked === true })
          }
          aria-label={milestone.is_complete ? "Mark incomplete" : "Mark complete"}
        />

        <input
          defaultValue={milestone.title}
          disabled={!canWrite}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v && v !== milestone.title) update.mutate({ id: milestone.id, payload: { title: v } });
          }}
          className={cn(
            "flex-1 bg-transparent text-sm focus:outline-none",
            milestone.is_complete && "text-muted-foreground line-through",
          )}
        />

        <input
          type="number"
          defaultValue={milestone.weight}
          min={0}
          max={100}
          disabled={!canWrite}
          onBlur={(e) => {
            const v = Number(e.target.value);
            if (v !== milestone.weight) update.mutate({ id: milestone.id, payload: { weight: v } });
          }}
          // Tabular weight cell — intentionally shorter than the DS Input
          // form control so it sits in the row without overwhelming it.
          className="w-16 rounded-md border border-input bg-background px-1.5 py-0.5 text-right text-xs focus:outline-none focus:ring-1 focus:ring-ring"
          title="Weight %"
        />

        <DatePicker
          aria-label="Due date"
          disabled={!canWrite}
          value={isoToDate(milestone.due_date)}
          onChange={(d) => {
            const v = dateToIso(d);
            if (v !== milestone.due_date) update.mutate({ id: milestone.id, payload: { due_date: v } });
          }}
        />

        <button
          type="button"
          onClick={() => setShowNotes((s) => !s)}
          className="text-xs text-brand hover:underline"
        >
          {milestone.notes ? "Notes" : "+ Note"}
        </button>

        {canWrite && (
          <button
            type="button"
            onClick={() => remove.mutate(milestone.id)}
            className="text-muted-foreground hover:text-destructive"
            aria-label="Delete milestone"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {showNotes && (
        <NotesField
          value={milestone.notes ?? ""}
          disabled={!canWrite}
          placeholder="Milestone note…"
          onSave={(v) => update.mutate({ id: milestone.id, payload: { notes: v } })}
        />
      )}
    </div>
  );
}
